/**
 * Тон фотографии — средний цвет и яркость (изменение v1.15).
 *
 * Обложка профиля берёт фон из аватара, как шапка профиля в Telegram: снимок
 * размыт, а цвет подложки и сила затемнения под белым текстом подстраиваются
 * под сам снимок — на светлом фото тень гуще, на тёмном почти не нужна.
 *
 * Снимок уменьшается до нескольких пикселей на canvas. Если источник не отдаёт
 * CORS-заголовков, холст «испорчен» и `getImageData` бросает — тогда тона нет и
 * обложка остаётся на запасной палитре: это украшение, а не данные.
 */
import { useEffect, useState } from 'react'

export interface ImageTone {
  /** Средний цвет снимка, `rgb(r g b)`. */
  color: string
  /** Тот же цвет темнее — нижний край градиента подложки. */
  deep: string
  /** Относительная яркость 0…1 (WCAG). */
  luminance: number
}

const SAMPLE = 12
const cache = new Map<string, ImageTone | null>()
const pending = new Map<string, Promise<ImageTone | null>>()

function channel(value: number): number {
  const c = value / 255
  return c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4
}

/** Относительная яркость по WCAG 2.x. */
export function relativeLuminance(r: number, g: number, b: number): number {
  return 0.2126 * channel(r) + 0.7152 * channel(g) + 0.0722 * channel(b)
}

/** Средний цвет по байтам RGBA; прозрачные пиксели не считаются. */
export function averageTone(data: ArrayLike<number>): ImageTone | null {
  let r = 0
  let g = 0
  let b = 0
  let count = 0
  for (let i = 0; i + 3 < data.length; i += 4) {
    if ((data[i + 3] ?? 0) < 16) continue
    r += data[i] ?? 0
    g += data[i + 1] ?? 0
    b += data[i + 2] ?? 0
    count += 1
  }
  if (!count) return null

  r = Math.round(r / count)
  g = Math.round(g / count)
  b = Math.round(b / count)
  const deep = (value: number): number => Math.round(value * 0.55)

  return {
    color: `rgb(${r} ${g} ${b})`,
    deep: `rgb(${deep(r)} ${deep(g)} ${deep(b)})`,
    luminance: relativeLuminance(r, g, b),
  }
}

function sample(url: string): Promise<ImageTone | null> {
  return new Promise((resolve) => {
    const image = new Image()
    image.crossOrigin = 'anonymous'
    image.decoding = 'async'
    image.onload = () => {
      try {
        const canvas = document.createElement('canvas')
        canvas.width = SAMPLE
        canvas.height = SAMPLE
        const context = canvas.getContext('2d', { willReadFrequently: true })
        if (!context) return resolve(null)
        context.drawImage(image, 0, 0, SAMPLE, SAMPLE)
        resolve(averageTone(context.getImageData(0, 0, SAMPLE, SAMPLE).data))
      } catch {
        // Источник без CORS: холст испорчен, тон недоступен
        resolve(null)
      }
    }
    image.onerror = () => resolve(null)
    image.src = url
  })
}

export function loadImageTone(url: string): Promise<ImageTone | null> {
  if (cache.has(url)) return Promise.resolve(cache.get(url) ?? null)
  const inFlight = pending.get(url)
  if (inFlight) return inFlight

  const request = sample(url).then((tone) => {
    cache.set(url, tone)
    pending.delete(url)
    return tone
  })
  pending.set(url, request)
  return request
}

/** Тон снимка для обложки; `null`, пока не посчитан или если посчитать нельзя. */
export function useImageTone(url: string | null): ImageTone | null {
  const [tone, setTone] = useState<ImageTone | null>(() => (url ? (cache.get(url) ?? null) : null))

  useEffect(() => {
    if (!url || typeof document === 'undefined') {
      setTone(null)
      return
    }
    let alive = true
    void loadImageTone(url).then((value) => {
      if (alive) setTone(value)
    })
    return () => {
      alive = false
    }
  }, [url])

  return tone
}
